import React, { useState, useEffect } from 'react';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { apiClient, type User } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import InviteUserModal from '../components/Users/InviteUserModal.tsx';
import EditUserModal from '../components/Users/EditUserModal.tsx';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Load users
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getUsers();
      if (response.data) {
        setUsers(response.data);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Handle user deletion
  const handleDeleteUser = async (user: User) => {
    // Prevent deleting current user
    if (currentUser?.id === user.id) {
      alert('You cannot delete your own account.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${user.email}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.deleteUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setError(err.message || 'Failed to delete user');
    }
  };

  // Handle status toggle
  const handleToggleUserStatus = async (user: User) => {
    // Prevent changing current user's status
    if (currentUser?.id === user.id) {
      alert('You cannot change your own account status.');
      return;
    }

    const newStatus = user.isActive ? 'inactive' : 'active';
    const newIsActive = !user.isActive;

    try {
      await apiClient.updateUser(user.id, { isActive: newIsActive, status: newStatus });
      setUsers(prev => prev.map(u => 
        u.id === user.id 
          ? { ...u, isActive: newIsActive, status: newStatus }
          : u
      ));
    } catch (err: any) {
      console.error('Failed to update user status:', err);
      setError(err.message || 'Failed to update user status');
    }
  };

  // Handle resend invitation
  const handleResendInvitation = async (user: User) => {
    // Prevent resending invitation for current user
    if (currentUser?.id === user.id) {
      alert('You cannot resend invitation for your own account.');
      return;
    }

    try {
      const result = await apiClient.resendInvitation(user.id);
      if (result.success && result.invitationLink) {
        // Copy to clipboard
        await navigator.clipboard.writeText(result.invitationLink);
        alert('Invitation resent! Link copied to clipboard.');
      }
    } catch (err: any) {
      console.error('Failed to resend invitation:', err);
      setError(err.message || 'Failed to resend invitation');
    }
  };

  // Handle invite success
  const handleInviteSuccess = (newUser: User, invitationLink?: string) => {
    setUsers(prev => [...prev, newUser]);
    setInviteModalOpen(false);
    
    if (invitationLink) {
      // Copy to clipboard and show message
      navigator.clipboard.writeText(invitationLink).then(() => {
        alert('User invited successfully! Invitation link copied to clipboard.');
      });
    }
  };

  // Handle edit success
  const handleEditSuccess = (updatedUser: User) => {
    setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  // Get status badge
  const getStatusBadge = (user: User) => {
    if (user.status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <ClockCircleOutlined className="mr-1" />
          Pending
        </span>
      );
    } else if (user.status === 'active' && user.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleOutlined className="mr-1" />
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <StopOutlined className="mr-1" />
          Inactive
        </span>
      );
    }
  };

  // Get role badge
  const getRoleBadge = (role: string) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        role === 'admin' 
          ? 'bg-blue-100 text-blue-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {role === 'admin' ? 'Admin' : 'Editor'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-6">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="admin-card mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-9 flex items-center">
              <UserOutlined className="mr-3" />
              User Management
            </h1>
            <p className="text-gray-6 mt-1">
              Manage user accounts and permissions
            </p>
          </div>
          
          <button
            onClick={() => setInviteModalOpen(true)}
            className="flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
          >
            <PlusOutlined className="mr-2" />
            Invite User
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="admin-card bg-red-50 border border-red-200 mb-6">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 underline mt-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="admin-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-9">
            Users ({users.length})
          </h2>
          <button
            onClick={loadUsers}
            className="flex items-center px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ReloadOutlined className="mr-1" />
            Refresh
          </button>
        </div>

        {users.length === 0 ? (
          <div className="text-center py-12">
            <UserOutlined className="text-4xl text-gray-4 mb-4" />
            <p className="text-gray-6">No users found</p>
            <button
              onClick={() => setInviteModalOpen(true)}
              className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              Invite First User
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-7">User</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-7">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-7">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-7">Last Login</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-7">Created</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-7">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <UserOutlined className="text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-9">{user.email}</p>
                          <p className="text-sm text-gray-6">ID: {user.id.substring(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(user)}
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-gray-7">
                        {user.lastLogin 
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : 'Never'
                        }
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-gray-7">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center space-x-2">
                        {/* Edit Button */}
                        <button
                          onClick={() => {
                            if (currentUser?.id === user.id) {
                              alert('You cannot edit your own account.');
                              return;
                            }
                            setSelectedUser(user);
                            setEditModalOpen(true);
                          }}
                          disabled={currentUser?.id === user.id}
                          className={`p-2 rounded transition-colors ${
                            currentUser?.id === user.id
                              ? 'text-gray-400 cursor-not-allowed opacity-50'
                              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title={
                            currentUser?.id === user.id
                              ? 'Cannot edit your own account'
                              : 'Edit User'
                          }
                        >
                          <EditOutlined />
                        </button>

                        {/* Toggle Status */}
                        <button
                          onClick={() => handleToggleUserStatus(user)}
                          disabled={currentUser?.id === user.id}
                          className={`p-2 rounded transition-colors ${
                            currentUser?.id === user.id
                              ? 'text-gray-400 cursor-not-allowed opacity-50'
                              : user.isActive
                                ? 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                                : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                          }`}
                          title={
                            currentUser?.id === user.id
                              ? 'Cannot change your own status'
                              : user.isActive 
                                ? 'Deactivate User' 
                                : 'Activate User'
                          }
                        >
                          {user.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                        </button>

                        {/* Resend Invitation (for pending users) */}
                        {user.status === 'pending' && (
                          <button
                            onClick={() => handleResendInvitation(user)}
                            disabled={currentUser?.id === user.id}
                            className={`p-2 rounded transition-colors ${
                              currentUser?.id === user.id
                                ? 'text-gray-400 cursor-not-allowed opacity-50'
                                : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                            }`}
                            title={
                              currentUser?.id === user.id
                                ? 'Cannot resend invitation for your own account'
                                : 'Resend Invitation'
                            }
                          >
                            <MailOutlined />
                          </button>
                        )}

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={currentUser?.id === user.id}
                          className={`p-2 rounded transition-colors ${
                            currentUser?.id === user.id
                              ? 'text-gray-400 cursor-not-allowed opacity-50'
                              : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
                          }`}
                          title={
                            currentUser?.id === user.id
                              ? 'Cannot delete your own account'
                              : 'Delete User'
                          }
                        >
                          <DeleteOutlined />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />

      {/* Edit User Modal */}
      <EditUserModal
        open={editModalOpen}
        user={selectedUser}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default Users;
